// Client-side file rules of the upload fields: accepted types, sizes and the
// image checks (dimensions, logos on the navy band).
import {
  DocumentCraftingFormField,
  DocumentCraftingImageMeta,
} from "../../../Models/documentCrafting";
import { LocalImageInfo } from "./documentFormModel";

/** como navy (`como.ci.*.yaml`): the band partner logos sit on. */
export const COMO_NAVY = "#032E4D";

/** Mean luminance (0–255) below which a logo on transparency counts as dark
 * (the threshold of the server's form warnings). */
export const DARK_LOGO_LUMINANCE = 128;

const MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
};

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif"];

export const normalizeExtension = (extension: string) => {
  const value = extension.trim().toLowerCase();
  return value.startsWith(".") ? value : `.${value}`;
};

export const fileExtension = (name: string) => {
  const index = name.lastIndexOf(".");
  return index < 0 ? "" : name.slice(index).toLowerCase();
};

export const isImageFileName = (name: string) =>
  IMAGE_EXTENSIONS.includes(fileExtension(name));

export const acceptedExtensions = (field: DocumentCraftingFormField) =>
  (field.accept ?? []).map(normalizeExtension);

/** react-dropzone `accept` (also filters the file dialog); undefined when an
 * extension has no known MIME type, then `matchesAccept` checks names. */
export const dropzoneAccept = (
  extensions: string[],
): Record<string, string[]> | undefined => {
  if (!extensions.length) return undefined;
  if (extensions.some((extension) => !MIME_BY_EXTENSION[extension])) return undefined;
  const accept: Record<string, string[]> = {};
  extensions.forEach((extension) => {
    const mime = MIME_BY_EXTENSION[extension];
    accept[mime] = [...(accept[mime] ?? []), extension];
  });
  return accept;
};

export const matchesAccept = (name: string, extensions: string[]) =>
  !extensions.length || extensions.includes(fileExtension(name));

/** Per-file limit: the field's own limit, capped by the workspace setting. */
export const maxFileMb = (
  field: DocumentCraftingFormField,
  workspaceMaxMb: number | null | undefined,
): number | null => {
  const limits = [field.max_size_mb, workspaceMaxMb].filter(
    (value): value is number => typeof value === "number" && value > 0,
  );
  return limits.length ? Math.min(...limits) : null;
};

/** "PNG, JPG" for the hint line. */
export const extensionList = (extensions: string[]) =>
  extensions.map((extension) => extension.replace(/^\./, "").toUpperCase()).join(", ");

export type LogoWarning = "dark" | "opaque";

/** Partner logos should be light on transparency for the navy band. */
export const logoWarning = (
  image: Pick<DocumentCraftingImageMeta, "has_alpha" | "mean_luminance"> | LocalImageInfo | null | undefined,
): LogoWarning | null => {
  if (!image || image.has_alpha === null || image.has_alpha === undefined) return null;
  if (!image.has_alpha) return "opaque";
  if (image.mean_luminance !== null && image.mean_luminance !== undefined) {
    return image.mean_luminance < DARK_LOGO_LUMINANCE ? "dark" : null;
  }
  return null;
};

/**
 * Reads the size of an image file and, where the browser allows, whether it
 * has transparency and how bright its visible pixels are (like the backend's
 * `image_meta`). Resolves null when the file is no readable image.
 */
export const readImageInfo = (file: File): Promise<LocalImageInfo | null> =>
  new Promise((resolve) => {
    if (typeof URL === "undefined" || !URL.createObjectURL) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const info: LocalImageInfo = {
        width_px: image.naturalWidth,
        height_px: image.naturalHeight,
        has_alpha: null,
        mean_luminance: null,
      };
      try {
        const scale = Math.min(1, 128 / Math.max(image.naturalWidth, image.naturalHeight, 1));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext("2d");
        if (context) {
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
          let alpha = false;
          let sum = 0;
          let visible = 0;
          for (let index = 0; index < data.length; index += 4) {
            const a = data[index + 3];
            if (a < 250) alpha = true;
            if (a > 20) {
              sum += 0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2];
              visible += 1;
            }
          }
          info.has_alpha = alpha;
          info.mean_luminance = visible ? sum / visible : null;
        }
      } catch {
        // No pixel access (e.g. in tests); the dimensions are enough.
      }
      URL.revokeObjectURL(url);
      resolve(info);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
